import React, { useEffect, useRef, useState } from "react";
import { LocateFixed, Loader2, Radio, ShieldCheck, Square } from "lucide-react";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";

const SEND_INTERVAL_MS = 8000;

function requestPosition() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      maximumAge: 3000,
      timeout: 15000,
    });
  });
}

function readableGpsError(error) {
  if (error?.code === 1) return "Autorisation GPS refusée. Autorisez la localisation pour ce site dans les réglages du navigateur.";
  if (error?.code === 2) return "Position indisponible. Vérifiez que la localisation du téléphone est activée.";
  if (error?.code === 3) return "Le GPS met trop de temps à répondre. Placez-vous près d'une fenêtre puis réessayez.";
  return error?.message || "Impossible d'obtenir la position GPS.";
}

export default function DriverLocationControl({ mission, onChanged }) {
  const watchIdRef = useRef(null);
  const sessionIdRef = useRef(null);
  const lastSentAtRef = useRef(0);
  const [sharing, setSharing] = useState(false);
  const [serverActive, setServerActive] = useState(Boolean(mission.tracking_active));
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState("");
  const [lastSentAt, setLastSentAt] = useState(null);
  const [accuracy, setAccuracy] = useState(null);

  useEffect(() => {
    setServerActive(Boolean(mission.tracking_active));
  }, [mission.tracking_active]);

  useEffect(() => () => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
  }, []);

  const stopLocalWatch = () => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    watchIdRef.current = null;
    sessionIdRef.current = null;
    setSharing(false);
  };

  const sendPosition = async (position, force = false) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    const now = Date.now();
    if (!force && now - lastSentAtRef.current < SEND_INTERVAL_MS) return;
    lastSentAtRef.current = now;
    try {
      await api.post(`/missions/${mission.id}/position`, {
        session_id: sessionId,
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy_m: position.coords.accuracy ?? null,
        speed_mps: position.coords.speed ?? null,
        heading_deg: position.coords.heading ?? null,
      });
      setAccuracy(position.coords.accuracy ?? null);
      setLastSentAt(new Date());
      setError("");
    } catch (requestError) {
      const status = requestError?.response?.status;
      if (status === 403 || status === 409) {
        stopLocalWatch();
        setServerActive(false);
      }
      setError(formatApiError(requestError));
    }
  };

  const stopSharing = async () => {
    setStopping(true);
    stopLocalWatch();
    try {
      await api.post(`/missions/${mission.id}/tracking/stop`);
      setServerActive(false);
      setError("");
      await onChanged?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setStopping(false);
    }
  };

  const startSharing = async () => {
    if (!navigator.geolocation) {
      setError("La géolocalisation n'est pas disponible sur cet appareil.");
      return;
    }
    if (!window.isSecureContext && window.location.hostname !== "localhost") {
      setError("Le suivi GPS nécessite une connexion HTTPS sécurisée.");
      return;
    }
    setStarting(true);
    setError("");
    try {
      // Le navigateur demande d'abord l'autorisation : aucun partage serveur
      // n'est activé si la personne refuse ou si le GPS est indisponible.
      const firstPosition = await requestPosition();
      const { data } = await api.post(`/missions/${mission.id}/tracking/start`, { consent: true });
      sessionIdRef.current = data.session_id;
      setSharing(true);
      setServerActive(true);
      await sendPosition(firstPosition, true);
      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => { sendPosition(position); },
        (gpsError) => { setError(readableGpsError(gpsError)); },
        { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 },
      );
      await onChanged?.();
    } catch (requestError) {
      stopLocalWatch();
      setError(requestError?.response ? formatApiError(requestError) : readableGpsError(requestError));
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="rounded-md border border-blue-200 bg-blue-50 p-3" data-testid="driver-location-control">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 rounded-full bg-blue-100 p-1.5 text-blue-700">
          {sharing ? <Radio className="h-4 w-4" /> : <LocateFixed className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-blue-950">Localisation conducteur</div>
          <p className="mt-1 text-xs leading-relaxed text-blue-900/80">
            Partage volontaire uniquement pour cette mission. Seule la dernière position est conservée et elle n'est visible que par l'expéditeur et votre entreprise.
          </p>
        </div>
      </div>

      {sharing ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2 text-xs font-medium text-blue-900">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-blue-600" />
            </span>
            Position partagée en direct
          </div>
          <div className="text-[11px] text-blue-800/75">
            {lastSentAt ? `Dernier envoi à ${lastSentAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : "Premier point GPS en cours…"}
            {accuracy != null && ` · précision ${Math.round(accuracy)} m`}
          </div>
          <Button type="button" variant="outline" onClick={stopSharing} disabled={stopping}
            className="h-10 w-full gap-2 border-blue-300 bg-white text-blue-950 hover:bg-blue-100">
            {stopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-3.5 w-3.5 fill-current" />}
            Arrêter le partage
          </Button>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {serverActive && (
            <div className="rounded bg-amber-100 px-2 py-1.5 text-xs text-amber-900">
              Une ancienne session est encore marquée active. Reprenez le partage pour envoyer une position fraîche, ou arrêtez-la.
            </div>
          )}
          <Button type="button" onClick={startSharing} disabled={starting || stopping}
            className="h-11 w-full gap-2 rounded-md bg-blue-700 text-white hover:bg-blue-800">
            {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {serverActive ? "Reprendre le partage GPS" : "Autoriser et partager ma position"}
          </Button>
          {serverActive && (
            <Button type="button" variant="ghost" onClick={stopSharing} disabled={stopping}
              className="h-9 w-full text-xs text-blue-900 hover:bg-blue-100">
              Arrêter l'ancienne session
            </Button>
          )}
        </div>
      )}

      {error && <div role="alert" className="mt-2 rounded bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div>}
      <p className="mt-2 text-[11px] leading-relaxed text-blue-800/70">
        Le suivi s'arrête automatiquement à la livraison. Gardez cette page ouverte pendant le trajet pour maintenir les mises à jour sur le Web.
      </p>
    </div>
  );
}
